import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { LandingPage } from './components/LandingPage';
import { AppLayout } from './components/AppLayout';
import { Contract, Language, AnalysisState, PreviewState, ReviewConfig } from './types';
import { analyzeFullContract } from './services/aiService';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { policyService } from './services/policyService';
import { contractService } from './services/contractService';

// 声明外部库 mammoth
declare const mammoth: any;

const AppContent: React.FC = () => {
  const { lang, toggleLang, t } = useLanguage();
  const [history, setHistory] = useState<Contract[]>([]);
  const [activeContract, setActiveContract] = useState<Contract | null>(null);
  const [analysisState, setAnalysisState] = useState<AnalysisState>({
    isAnalyzing: false,
    progress: 0,
    currentClauseIndex: 0
  });
  const [companyPolicy, setCompanyPolicy] = useState('');
  const [selectedClauseIndex, setSelectedClauseIndex] = useState<number | null>(null);

  // New: Preview state for pre-analysis configuration
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const [pendingDbContractId, setPendingDbContractId] = useState<string | null>(null);
  /** 从历史记录点进已完成合同并展示结果时为 true；新建/分析中/刚分析完 为 false */
  const [viewingHistoryResult, setViewingHistoryResult] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, signInWithGoogle, loading } = useAuth();

  // Fetch Policy and Contract History when User logs in
  React.useEffect(() => {
    if (user) {
      // Load policy
      policyService.getUserPolicy().then(policy => {
        if (policy) {
          setCompanyPolicy(policy.content);
        } else {
          setCompanyPolicy('');
        }
      });

      // Load contract history from database
      contractService.getUserContracts().then(async (dbContracts) => {
        const contractsWithDetails = await Promise.all(
          dbContracts.slice(0, 20).map(async (dbContract) => {
            const details = await contractService.getContractWithDetails(dbContract.id);
            const clauses = details?.risks?.map((risk, i) => ({
              clauseId: `db-${i}`,
              originalText: risk.original_text,
              category: '',
              riskLevel: risk.risk_level,
              riskDescription: risk.description,
              suggestedAction: risk.suggestion,
              revisedText: risk.revised_text || ''
            })) || [];

            return {
              id: dbContract.id,
              name: dbContract.title,
              uploadDate: new Date(dbContract.created_at).toLocaleDateString(),
              status: dbContract.status === 'completed' ? 'completed' :
                dbContract.status === 'failed' ? 'error' : 'analyzing',
              clauses,
              summary: dbContract.summary,
              storagePath: dbContract.storage_path
              // Content loaded on demand
            } as Contract;
          })
        );
        setHistory(contractsWithDetails);

        // Hydrate active contract if needed
        // If we just reloaded and there is an active contract in URL (future feature), we could load it here.
      });
    } else {
      setCompanyPolicy('');
      setHistory([]);
    }
  }, [user]);

  const parseDocx = async (file: File | Blob): Promise<{ text: string[]; html: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        try {
          // Double parsing: Text for analysis, HTML for display
          const [textResult, htmlResult] = await Promise.all([
            mammoth.extractRawText({ arrayBuffer }),
            mammoth.convertToHtml({
              arrayBuffer,
              // Enhanced style mapping for better format preservation
              styleMap: [
                "p[style-name='Heading 1'] => h1:fresh",
                "p[style-name='Heading 2'] => h2:fresh",
                "p[style-name='Heading 3'] => h3:fresh",
                "p[style-name='heading 1'] => h1:fresh",
                "p[style-name='heading 2'] => h2:fresh",
                "p[style-name='heading 3'] => h3:fresh",
                "r[style-name='Strong'] => strong",
                "r[style-name='Emphasis'] => em"
              ],
              // Preserve basic formatting marks
              convertImage: mammoth.images.imgElement((image: any) => {
                return image.read("base64").then((imageBuffer: string) => {
                  return {
                    src: "data:" + image.contentType + ";base64," + imageBuffer
                  };
                });
              })
            })
          ]);

          const paragraphs = textResult.value
            .split('\n')
            .map((p: string) => p.trim())
            .filter((p: string) => p.length > 0);

          resolve({ text: paragraphs, html: htmlResult.value });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 如果未登录，先触发登录
    if (!user) {
      signInWithGoogle();
      return;
    }

    // Navigate to app if not already there
    navigate('/app');

    try {
      // Only parse the file, don't start analysis yet
      const { text: actualClausesText, html: htmlContent } = await parseDocx(file);

      // Set preview state for configuration
      setPreviewState({
        fileName: file.name,
        content: actualClausesText,
        htmlContent,
        fileObject: file, // Store file object for later upload
        isReady: true
      });

      // Clear any previous active contract
      setActiveContract(null);
      setViewingHistoryResult(false);
      setAnalysisState({ isAnalyzing: false, progress: 0, currentClauseIndex: 0 });

    } catch (error: any) {
      console.error("File parsing error:", error);
      alert(error.message || (lang === 'zh' ? "文件解析异常" : "File Parsing Error"));
    }
  };

  // New: Start analysis with user-selected configuration
  const handleStartAnalysis = async (config: ReviewConfig) => {
    if (!previewState) return;

    setViewingHistoryResult(false);
    setAnalysisState({ isAnalyzing: true, progress: 0, currentClauseIndex: 0 });

    try {
      // Create contract in database
      const dbContract = await contractService.createContract(previewState.fileName, config.policyId);
      setPendingDbContractId(dbContract?.id || null);

      // Upload file if contract created successfully and file exists
      if (dbContract && previewState.fileObject) {
        try {
          const storagePath = await contractService.uploadContractFile(dbContract.id, previewState.fileObject);
          if (storagePath) {
            await contractService.updateContract(dbContract.id, { storage_path: storagePath });
          }
        } catch (uploadError) {
          console.error("File upload failed, but analysis continues:", uploadError);
        }
      }

      const initialContract: Contract = {
        id: dbContract?.id || Math.random().toString(36).substr(2, 9),
        name: previewState.fileName,
        uploadDate: new Date().toLocaleDateString(),
        status: 'analyzing',
        clauses: [],
        fullContent: previewState.content,
        displayHtml: previewState.htmlContent
      };

      setActiveContract(initialContract);

      // 保存需要的数据，然后再清空 preview state
      const contentToAnalyze = previewState.content;
      setPreviewState(null);

      const substansiveClauses = contentToAnalyze.filter(p => p.length > 30);
      const totalToAnalyze = Math.min(substansiveClauses.length, 15);
      const clausesToAnalyze = substansiveClauses.slice(0, totalToAnalyze);

      setAnalysisState(prev => ({ ...prev, currentClauseIndex: 0, progress: 0 }));

      // Pass config to analysis (position, intensity, policy)
      const { analyses, summary, legalReferences } = await analyzeFullContract(clausesToAnalyze, lang, config.policyContent || '', config);

      // 逐个添加条款，实现实时更新效果
      const processedClauses: any[] = [];
      const totalClauses = analyses.length;

      for (let i = 0; i < totalClauses; i++) {
        const analysis = analyses[i];
        const originalText = clausesToAnalyze[i];
        const processedClause = {
          clauseId: `real-${i}`,
          originalText: originalText || '',
          ...analysis as any
        };

        processedClauses.push(processedClause);

        // 平滑更新进度条和当前条款索引
        // 从0%开始，分析过程占90%，打字机完成最后10%
        const baseProgress = 0;
        const progressRange = 90;
        const targetProgress = baseProgress + Math.floor((i + 1) / totalClauses * progressRange);
        const finalProgress = Math.min(targetProgress, 90);

        // 实时更新合同条款，让 RevealPath 能看到数字变化
        setActiveContract(prev => prev ? ({
          ...prev,
          clauses: [...processedClauses]
        }) : null);

        // 平滑更新进度和当前条款索引
        setAnalysisState(prev => ({
          ...prev,
          currentClauseIndex: i + 1,
          progress: finalProgress
        }));

        // 轻微延迟，兼顾实时观感与审查速度（原 200ms 已大幅缩短）
        await new Promise(resolve => setTimeout(resolve, 30));
      }

      setAnalysisState(prev => ({ ...prev, progress: 90 }));

      // Save risks to database
      if (dbContract) {
        const risksToSave = processedClauses.map(clause => ({
          original_text: clause.originalText,
          risk_level: clause.riskLevel as 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW',
          description: clause.riskDescription || '',
          suggestion: clause.suggestedAction || '',
          revised_text: clause.revisedText
        }));
        await contractService.saveRisks(dbContract.id, risksToSave);

        // Save thought chains to analysis_steps
        for (let i = 0; i < processedClauses.length; i++) {
          const clause = processedClauses[i];
          if (clause.thoughtChain) {
            // 将思维链按行分割成数组
            const thoughts = clause.thoughtChain.split('\n').filter(line => line.trim().length > 0);
            await contractService.upsertAnalysisStep(
              dbContract.id,
              i + 1,
              lang === 'zh' ? `分析条款 ${i + 1}` : `Analyze Clause ${i + 1}`,
              'completed',
              { thoughts, duration: 0 }
            );
          }
        }

        await contractService.updateContract(dbContract.id, { status: 'completed', summary });

        // Save parsed content to storage so it can be loaded later even if file is missing
        try {
          await contractService.saveParsedContent(dbContract.id, initialContract.fullContent, initialContract.displayHtml || '');
          console.log('[App] 已保存解析后的内容到存储');
        } catch (contentError) {
          console.warn('[App] 保存解析内容失败，但不影响分析结果:', contentError);
        }
      }

      const completedContract: Contract = {
        ...initialContract,
        status: 'completed',
        clauses: processedClauses,
        summary,
        displayHtml: initialContract.displayHtml, // 确保 displayHtml 被保留
        fullContent: initialContract.fullContent,   // 确保 fullContent 被保留
        legalReferences: legalReferences // 保存法律条款引用
      };

      console.log(`[App] 分析完成，合同包含 ${processedClauses.length} 个条款分析`);
      console.log(`[App] displayHtml 存在: ${!!completedContract.displayHtml}`);
      console.log(`[App] fullContent 存在: ${!!completedContract.fullContent}`);

      setActiveContract(completedContract);
      setHistory(prev => [completedContract, ...prev]);
      setAnalysisState({ isAnalyzing: false, progress: 100, currentClauseIndex: totalToAnalyze });
      setPendingDbContractId(null);
    } catch (error: any) {
      console.error("Critical processing error:", error);
      alert(error.message || (lang === 'zh' ? "解析异常" : "Analysis Error"));
      setAnalysisState(prev => ({ ...prev, isAnalyzing: false }));
      setActiveContract(null);
      setPendingDbContractId(null);
    }
  };

  const handleDownload = async () => {
    if (!activeContract) return;

    try {
      // 使用与 DocumentViewer 完全相同的逻辑生成 HTML 内容
      let htmlContent = '';
      const clauses = activeContract.clauses || [];

      // 复用 DocumentViewer 的 displayHtml 计算逻辑
      if (activeContract.displayHtml) {
        // 如果有 displayHtml，使用 DOM 操作来添加批注（与 DocumentViewer 完全相同的方法）
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = activeContract.displayHtml;
        const allParagraphs = tempDiv.querySelectorAll('p');

        if (clauses.length > 0) {
          clauses.forEach((clause, clauseIndex) => {
            if (!clause.originalText || !clause.originalText.trim()) return;

            const clauseText = clause.originalText.trim();
            let matched = false;

            allParagraphs.forEach((pElement) => {
              if (matched || pElement.hasAttribute('data-clause-index')) return;

              const paragraphText = pElement.textContent || pElement.innerText || '';
              const cleanParagraphText = paragraphText.trim();

              // 与 DocumentViewer 完全相同的匹配策略
              const matchStrategies = [
                () => cleanParagraphText === clauseText,
                () => cleanParagraphText.includes(clauseText),
                () => clauseText.includes(cleanParagraphText) && cleanParagraphText.length > 20,
                () => {
                  const clausePrefix = clauseText.substring(0, Math.min(50, clauseText.length));
                  const paraPrefix = cleanParagraphText.substring(0, Math.min(50, cleanParagraphText.length));
                  return paraPrefix === clausePrefix && clausePrefix.length > 20;
                }
              ];

              for (const strategy of matchStrategies) {
                if (strategy()) {
                  matched = true;

                  // 添加风险级别类和标记
                  const riskClass = `risk-${clause.riskLevel.toLowerCase()}`;
                  const existingClass = pElement.getAttribute('class') || '';
                  const newClass = `${existingClass} ${riskClass}`.trim();
                  pElement.setAttribute('class', newClass);
                  pElement.setAttribute('data-clause-index', clauseIndex.toString());

                  // 添加风险分析信息（与 DocumentViewer 完全相同的格式）
                  if (clause.riskDescription || clause.suggestedAction) {
                    const analysisDiv = document.createElement('div');
                    analysisDiv.className = 'revision-suggestion';
                    analysisDiv.style.cssText = 'margin-top: 12px; margin-bottom: 20px; padding: 16px; background-color: rgba(59, 130, 246, 0.08); border-left: 4px solid #3b82f6; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.05);';

                    // 风险描述部分（使用与 DocumentViewer 相同的格式）
                    if (clause.riskDescription) {
                      const riskTitleP = document.createElement('p');
                      riskTitleP.style.cssText = 'font-size: 11pt; color: #1e40af; font-weight: bold; margin: 0 0 8px 0;';
                      riskTitleP.textContent = t('risk.analysis') + '：';

                      const riskContentP = document.createElement('div');
                      riskContentP.style.cssText = 'font-size: 10pt; color: #1e293b; line-height: 1.6; margin: 0 0 12px 0; white-space: pre-wrap;';
                      riskContentP.textContent = clause.riskDescription.replace(/\\n/g, '\n');

                      analysisDiv.appendChild(riskTitleP);
                      analysisDiv.appendChild(riskContentP);
                    }

                    // 建议部分（使用与 DocumentViewer 相同的格式）
                    if (clause.suggestedAction) {
                      const suggestionTitleP = document.createElement('p');
                      suggestionTitleP.style.cssText = 'font-size: 11pt; color: #1e40af; font-weight: bold; margin: 0 0 8px 0;';
                      suggestionTitleP.textContent = t('risk.suggestion') + '：';

                      const suggestionContentP = document.createElement('div');
                      suggestionContentP.style.cssText = 'font-size: 10pt; color: #1e293b; line-height: 1.6; margin: 0; white-space: pre-wrap;';
                      suggestionContentP.textContent = clause.suggestedAction.replace(/\\n/g, '\n');

                      analysisDiv.appendChild(suggestionTitleP);
                      analysisDiv.appendChild(suggestionContentP);
                    }

                    pElement.parentNode?.insertBefore(analysisDiv, pElement.nextSibling);
                  }

                  return;
                }
              }
            });
          });
        }

        htmlContent = tempDiv.innerHTML;
      } else if (activeContract.fullContent) {
        // 从 fullContent 生成 HTML，并添加修改建议和风险标记（与 DocumentViewer 完全相同的格式）
        htmlContent = activeContract.fullContent.map((paragraph, index) => {
          const paragraphText = paragraph.trim();
          const clause = clauses.find(c => {
            const clauseText = c.originalText.trim();
            return clauseText === paragraphText || paragraphText.includes(clauseText.substring(0, 30));
          });

          const clauseIndex = clause ? clauses.indexOf(clause) : null;
          const dataAttr = clauseIndex !== null ? `data-clause-index="${clauseIndex}"` : '';
          const riskClass = clause ? `risk-${clause.riskLevel.toLowerCase()}` : '';

          // 构建段落 HTML - 包含风险标记
          let paragraphHtml = `<p ${dataAttr} class="${riskClass}">${paragraph.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;

          // 添加风险分析信息（与 DocumentViewer 完全相同的格式）
          if (clause && (clause.riskDescription || clause.suggestedAction)) {
            let analysisHtml = `<div class="revision-suggestion" style="margin-top: 12px; margin-bottom: 20px; padding: 16px; background-color: rgba(59, 130, 246, 0.08); border-left: 4px solid #3b82f6; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.05);">`;

            // 风险描述部分（使用与 DocumentViewer 相同的格式）
            if (clause.riskDescription) {
              const formattedRisk = (clause.riskDescription || '').replace(/\\n/g, '\n').replace(/\n/g, '<br>').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
              analysisHtml += `<p style="font-size: 11pt; color: #1e40af; font-weight: bold; margin: 0 0 8px 0;">${t('risk.analysis')}：</p>`;
              analysisHtml += `<div style="font-size: 10pt; color: #1e293b; line-height: 1.6; margin: 0 0 12px 0; white-space: pre-wrap;">${formattedRisk}</div>`;
            }

            // 建议部分（使用与 DocumentViewer 相同的格式）
            if (clause.suggestedAction) {
              const formattedSuggestion = (clause.suggestedAction || '').replace(/\\n/g, '\n').replace(/\n/g, '<br>').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
              analysisHtml += `<p style="font-size: 11pt; color: #1e40af; font-weight: bold; margin: 0 0 8px 0;">${t('risk.suggestion')}：</p>`;
              analysisHtml += `<div style="font-size: 10pt; color: #1e293b; line-height: 1.6; margin: 0; white-space: pre-wrap;">${formattedSuggestion}</div>`;
            }

            analysisHtml += `</div>`;
            paragraphHtml += analysisHtml;
          }

          return paragraphHtml;
        }).join('');
      } else {
        alert(lang === 'zh' ? '无法下载：文档内容不可用' : 'Cannot download: Document content not available');
        return;
      }

      // 确保 HTML 内容格式正确（移除可能存在的完整 HTML 结构，只保留 body 内容）
      let cleanHtmlContent = htmlContent;

      // 清理HTML内容，移除可能导致Word显示问题的元素
      const tempCleanDiv = document.createElement('div');
      tempCleanDiv.innerHTML = cleanHtmlContent;

      // 移除可能导致问题的内联样式和元素
      tempCleanDiv.querySelectorAll('*').forEach((el) => {
        const htmlEl = el as HTMLElement;

        // 确保所有段落都有正确的样式
        if (htmlEl.tagName === 'P') {
          const isRiskParagraph = htmlEl.classList.contains('risk-critical') ||
            htmlEl.classList.contains('risk-high') ||
            htmlEl.classList.contains('risk-medium') ||
            htmlEl.classList.contains('risk-low');

          if (!isRiskParagraph) {
            // 普通段落：两端对齐
            htmlEl.style.textAlign = 'justify';
            htmlEl.style.setProperty('text-justify', 'inter-ideograph');
          }

          // 统一段落样式（与 DocumentViewer 一致）
          htmlEl.style.margin = htmlEl.style.margin || '0 0 0.5em 0';
          htmlEl.style.padding = htmlEl.style.padding || '0';
          htmlEl.style.lineHeight = htmlEl.style.lineHeight || '1.5';
          htmlEl.style.fontSize = htmlEl.style.fontSize || '11pt';
          htmlEl.style.color = htmlEl.style.color || '#000000';
          htmlEl.style.verticalAlign = 'baseline';
        }

        // 处理div元素
        if (htmlEl.tagName === 'DIV') {
          // 移除可能导致错位的定位样式
          if (htmlEl.style.position === 'absolute' || htmlEl.style.position === 'fixed') {
            htmlEl.style.position = 'static';
          }
          // 确保块级元素正确显示
          if (htmlEl.style.display === 'inline' || htmlEl.style.display === 'inline-block') {
            if (!htmlEl.classList.contains('revision-suggestion')) {
              htmlEl.style.display = 'block';
            }
          }
          // 保留 revision-suggestion 的内联样式，不要覆盖
          if (htmlEl.classList.contains('revision-suggestion')) {
            // 确保保留所有内联样式，不进行覆盖
            // 内联样式已经在创建时设置好了，这里不做修改
          }
        }

        // 处理行内元素，确保不会错位
        if (['SPAN', 'EM', 'STRONG', 'B', 'I', 'U'].includes(htmlEl.tagName)) {
          htmlEl.style.display = 'inline';
          htmlEl.style.verticalAlign = 'baseline';
          htmlEl.style.position = 'static';
        }

        // 移除可能导致问题的transform和float
        if (htmlEl.style.transform) {
          htmlEl.style.transform = 'none';
        }
        if (htmlEl.style.float) {
          htmlEl.style.float = 'none';
        }
      });

      cleanHtmlContent = tempCleanDiv.innerHTML;

      // 如果包含完整的 HTML 结构，提取 body 内容
      if (htmlContent.includes('<body>')) {
        const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        if (bodyMatch) {
          cleanHtmlContent = bodyMatch[1];
        }
      } else if (htmlContent.includes('<html>')) {
        // 如果只有 html 标签，提取内容
        const htmlMatch = htmlContent.match(/<html[^>]*>([\s\S]*)<\/html>/i);
        if (htmlMatch) {
          cleanHtmlContent = htmlMatch[1];
        }
      }

      // 使用 exportService 导出为 DOCX（使用原来的格式）
      const { exportToDocx } = await import('./services/exportService');
      const fileName = activeContract.name.split('.')[0] + '_批注版';
      await exportToDocx(cleanHtmlContent, fileName, activeContract.summary || '');
    } catch (error: any) {
      console.error('Download error:', error);
      alert(error.message || (lang === 'zh' ? '下载失败' : 'Download failed'));
    }
  };

  // Add a global loading state to prevent flash of content
  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const handleNewProject = () => {
    setActiveContract(null);
    setPreviewState(null);
    setViewingHistoryResult(false);
    setAnalysisState({ isAnalyzing: false, progress: 0, currentClauseIndex: 0 });
  };

  // Lazy load content when selecting a contract from history
  const handleSelectContract = async (contract: Contract | null) => {
    if (!contract) {
      setActiveContract(null);
      setViewingHistoryResult(false);
      return;
    }
    // 不在此处统一 set false，避免覆盖：仅在下述分支里按需置 true

    console.log(`[App] 选择合同: ${contract.name}, fullContent: ${!!contract.fullContent}, displayHtml: ${!!contract.displayHtml}, storagePath: ${contract.storagePath}`);

    // If content is missing, try to hydrate
    if (!contract.fullContent || contract.fullContent.length === 0 || !contract.displayHtml) {
      const isCompletedHistory = contract.status === 'completed' && (contract.clauses?.length ?? 0) > 0;
      const noStoragePath = !contract.storagePath || !String(contract.storagePath).trim();

      // storage_path 为空（含空串/空格）且为已完成历史：不下载、不进入分析中，直接 loadParsedContent，进度 100%
      if (noStoragePath && isCompletedHistory) {
        const contractWithoutContent: Contract = { ...contract, fullContent: undefined, displayHtml: undefined };
        flushSync(() => {
          setViewingHistoryResult(true);
          setAnalysisState({ isAnalyzing: false, progress: 100, currentClauseIndex: 0 });
          setActiveContract(contractWithoutContent);
        });
        try {
          const parsed = await contractService.loadParsedContent(contract.id);
          if (parsed?.fullContent?.length) {
            const withContent: Contract = { ...contract, fullContent: parsed.fullContent, displayHtml: parsed.displayHtml };
            setHistory(prev => prev.map(c => c.id === contract.id ? withContent : c));
            setActiveContract(withContent);
          }
        } catch (e) {
          console.warn("[App] loadParsedContent 失败:", e);
        }
        return;
      }

      let path = noStoragePath ? undefined : (contract.storagePath?.trim() || undefined);
      if (!path && user) {
        path = `${user.id}/${contract.id}/${contract.name}`;
        console.log("[App] No storage path in DB, trying fallback path:", path);
      }

      if (path) {
        try {
          if (isCompletedHistory) setViewingHistoryResult(true);

          const tempContract = { ...contract, status: 'analyzing' as const };
          setActiveContract(tempContract);
          setAnalysisState({ isAnalyzing: true, progress: 0, currentClauseIndex: 0 });

          console.log("[App] 开始下载文件:", path);
          setAnalysisState({ isAnalyzing: true, progress: 30, currentClauseIndex: 0 });
          const blob = await contractService.downloadContractFile(path);

          if (blob) {
            setAnalysisState({ isAnalyzing: true, progress: 60, currentClauseIndex: 0 });
          }

          if (blob) {
            console.log("[App] 文件下载成功，开始解析...");
            const { text, html } = await parseDocx(blob);

            const updatedContract: Contract = {
              ...contract,
              fullContent: text,
              displayHtml: html,
              storagePath: path // Update local model with successful path
            };

            console.log(`[App] 解析完成: ${text.length} 个段落, HTML 长度: ${html.length}`);

            // Update history locally so next time we don't need to fetch
            setHistory(prev => prev.map(c => c.id === contract.id ? updatedContract : c));
            setActiveContract(updatedContract);
            setAnalysisState({ isAnalyzing: false, progress: 100, currentClauseIndex: 0 });
            setViewingHistoryResult(updatedContract.status === 'completed');
          } else {
            console.error("[App] 文件下载失败 (blob is null) for path:", path);
            if (isCompletedHistory) setViewingHistoryResult(true);
            setAnalysisState({ isAnalyzing: false, progress: 100, currentClauseIndex: 0 });

            console.log("[App] 尝试加载保存的解析内容...");
            try {
              const parsedContent = await contractService.loadParsedContent(contract.id);
              if (parsedContent && parsedContent.fullContent && parsedContent.fullContent.length > 0) {
                console.log("[App] ✓ 成功加载保存的解析内容:", parsedContent.fullContent.length, "个段落");
                const contractWithSavedContent: Contract = {
                  ...contract,
                  fullContent: parsedContent.fullContent,
                  displayHtml: parsedContent.displayHtml,
                  storagePath: path
                };
                setHistory(prev => prev.map(c => c.id === contract.id ? contractWithSavedContent : c));
                setActiveContract(contractWithSavedContent);
                setViewingHistoryResult(contractWithSavedContent.status === 'completed');
                return;
              }
              console.log("[App] 没有找到保存的解析内容");
            } catch (contentError) {
              console.warn("[App] 加载保存的解析内容失败:", contentError);
            }

            if (contract.clauses && contract.clauses.length > 0) {
              console.log("[App] 文件缺失但合同有分析数据，显示合同但无文档内容");
              const contractWithoutContent: Contract = {
                ...contract,
                fullContent: undefined,
                displayHtml: undefined
              };
              setActiveContract(contractWithoutContent);
              setViewingHistoryResult(contractWithoutContent.status === 'completed');
            } else {
              console.warn("[App] 既无文件也无分析数据");
              setActiveContract(contract);
              setViewingHistoryResult(contract.status === 'completed');
            }
          }
        } catch (error) {
          console.error("[App] 加载合同内容时出错:", error);
          setAnalysisState({ isAnalyzing: false, progress: 100, currentClauseIndex: 0 });
          if (isCompletedHistory) setViewingHistoryResult(true);
          if (contract.clauses && contract.clauses.length > 0) {
            const contractWithoutContent: Contract = {
              ...contract,
              fullContent: undefined,
              displayHtml: undefined
            };
            setActiveContract(contractWithoutContent);
            setViewingHistoryResult(contractWithoutContent.status === 'completed');
          } else {
            setActiveContract(contract);
            setViewingHistoryResult(contract.status === 'completed');
          }
        }
      } else {
        console.warn("[App] 没有存储路径，无法加载文档内容");
        setAnalysisState({ isAnalyzing: false, progress: 100, currentClauseIndex: 0 });
        if (contract.clauses && contract.clauses.length > 0) {
          const contractWithoutContent: Contract = {
            ...contract,
            fullContent: undefined,
            displayHtml: undefined
          };
          setActiveContract(contractWithoutContent);
          setViewingHistoryResult(contractWithoutContent.status === 'completed');
        } else {
          setActiveContract(contract);
          setViewingHistoryResult(contract.status === 'completed');
        }
      }
    } else {
      // 内容已存在，直接显示
      console.log("[App] 合同内容已存在，直接显示");
      setAnalysisState({ isAnalyzing: false, progress: 100, currentClauseIndex: 0 });
      setActiveContract(contract);
      setViewingHistoryResult(contract.status === 'completed');
    }
  };


  return (
    <Routes>
      <Route
        path="/"
        element={
          <LandingPage
            onToggleLang={toggleLang}
            onFileUpload={handleFileUpload}
            onEnterApp={() => {
              if (user) {
                navigate('/app');
              } else {
                signInWithGoogle();
              }
            }}
          />
        }
      />
      <Route
        path="/app"
        element={
          <AppLayout
            history={history}
            activeContract={activeContract}
            setActiveContract={handleSelectContract}
            analysisState={analysisState}
            viewingHistoryResult={viewingHistoryResult}
            handleFileUpload={handleFileUpload}
            handleDownload={handleDownload}
            companyPolicy={companyPolicy}
            setCompanyPolicy={setCompanyPolicy}
            selectedClauseIndex={selectedClauseIndex}
            setSelectedClauseIndex={setSelectedClauseIndex}
            previewState={previewState}
            handleStartAnalysis={handleStartAnalysis}
            onNewProject={handleNewProject}
          />
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
};

export default App;
